import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, RotateCcw, Trash2 } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

const SAMPLE_REGEX = "[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}";
const SAMPLE_TEXT = "Hi developer, contact us at support@247devtools.org or reach our technical department at core-team@dns-resolver.net for instant routing support!";

export function RegexTester() {
  const [regexStr, setRegexStr] = useState(SAMPLE_REGEX);
  const [flags, setFlags] = useState("g");
  const [text, setText] = useState(SAMPLE_TEXT);
  const [matches, setMatches] = useState<{match: string, index: number}[]>([]);
  const [error, setError] = useState<string | null>(null);

  const testRegex = (r = regexStr, f = flags, t = text) => {
    try {
      setError(null);
      if (!r) { setMatches([]); return; }
      const regex = new RegExp(r, f);
      const m = [];
      let match;
      if (f.includes('g')) {
        while ((match = regex.exec(t)) !== null) {
          m.push({ match: match[0], index: match.index });
          if (m.length > 1000) break; // safety limit
        }
      } else {
        match = regex.exec(t);
        if (match) m.push({ match: match[0], index: match.index });
      }
      setMatches(m);
    } catch(e:any) {
      setError(e.message);
    }
  };

  useEffect(() => {
    testRegex();
  }, []);

  return (
    <ToolPageLayout
      title="Regex Tester & Debugger Online - Test Regular Expressions"
      description="Test, validate, and debug your Javascript regular expressions in real-time. Matches characters, substrings, or email strings instantly with proper highlights."
      h1="Regex Tester"
      category="Regex"
      categoryPath="/regex-tester"
      howToUse={[
        "Type or paste your regular expression pattern inside the / slashes.",
        "Input modifiers/flags (e.g., 'g' for global match, 'i' for case-insensitive).",
        "Type your candidate test string in the text editor to see live extracted match lists."
      ]}
      faq={[
        { question: "What is a global match flag ('g')?", answer: "The 'g' flag specifies a global search, finding all matches rather than stopping at the first occurrence." },
        { question: "Is my regex testing private?", answer: "Yes. All computations are executed inside your browser RAM without cloud storage." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1 space-y-2">
            <label className="text-sm font-semibold text-slate-700">Regular Expression Pattern</label>
            <div className="flex items-center gap-2 bg-white border rounded-lg px-3 shadow-xs">
              <span className="text-slate-400 font-mono">/</span>
              <input 
                type="text" 
                value={regexStr} 
                onChange={e => { setRegexStr(e.target.value); testRegex(e.target.value, flags, text); }} 
                className="flex-1 min-w-0 bg-transparent py-2.5 outline-none font-mono text-sm" 
                placeholder="[a-z]+" 
              />
              <span className="text-slate-400 font-mono">/</span>
              <input 
                type="text" 
                value={flags} 
                onChange={e => { setFlags(e.target.value); testRegex(regexStr, e.target.value, text); }} 
                className="w-12 shrink-0 bg-transparent py-2.5 outline-none font-mono text-sm text-center font-bold text-slate-700" 
                placeholder="g" 
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => { setRegexStr(SAMPLE_REGEX); setFlags("g"); setText(SAMPLE_TEXT); testRegex(SAMPLE_REGEX, "g", SAMPLE_TEXT); }}
              className="h-10 gap-1.5"
            >
              <RotateCcw className="w-4 h-4" />
              Reset Sample
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => { setRegexStr(""); setFlags("g"); setText(""); setMatches([]); }}
              className="h-10 text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </Button>
          </div>
        </div>
        {error && <div className="text-red-600 font-medium text-xs bg-red-50 border border-red-100 p-3 rounded-lg">{error}</div>}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Test String Input</label>
            <Textarea value={text} onChange={e => { setText(e.target.value); testRegex(regexStr, flags, e.target.value); }} placeholder="Test string here..." className="min-h-[220px]" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Matches Found ({matches.length})</label>
            <div className="border border-slate-200 rounded-xl bg-slate-50 p-4 overflow-y-auto max-h-[220px] min-h-[220px]">
              {matches.length > 0 ? (
                <ul className="space-y-1.5">
                  {matches.map((m, i) => (
                    <li key={i} className="text-xs bg-white border border-slate-200 px-2 py-1.5 rounded-lg font-mono flex justify-between items-center">
                      <span className="text-indigo-600 font-bold">"{m.match}"</span>
                      <span className="text-slate-400 text-[10px]">Index: {m.index}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-slate-400 text-xs text-center pt-16 font-medium">No matches detected. Adjust pattern above.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function RegexCheatSheet() {
  const categories = [
    { name: "Character Classes", items: [ { k: ".", v: "Any character except newline" }, { k: "\\w", v: "Word character [a-zA-Z0-9_]" }, { k: "\\d", v: "Digit [0-9]" }, { k: "\\s", v: "Whitespace char" } ] },
    { name: "Anchors & Boundaries", items: [ { k: "^", v: "Start of line/string" }, { k: "$", v: "End of line/string" }, { k: "\\b", v: "Word boundary match" } ] },
    { name: "Quantifiers", items: [ { k: "*", v: "0 or more repetitions" }, { k: "+", v: "1 or more repetitions" }, { k: "?", v: "0 or 1 repetition" }, { k: "{3,5}", v: "Exactly 3 to 5 times" } ] }
  ];
  return (
    <ToolPageLayout
      title="Regex Cheat Sheet - Quick Reference Syntax Guide"
      description="A quick, high-speed cheatsheet reference for regular expression syntax. Character classes, boundaries, quantifiers, and common patterns."
      h1="Regex Cheat Sheet"
      category="Regex"
      categoryPath="/regex-tester"
      howToUse={[
        "Review syntax guides below for character classes, boundaries, and quantifier groups.",
        "Utilize symbols in your regular expression builders to match dynamic target data easily."
      ]}
      faq={[
        { question: "What does \\w match?", answer: "It matches any word character, which includes a-z, A-Z, 0-9, and underscore (_)." },
        { question: "What is an anchor?", answer: "Anchors do not match characters; instead, they check if the match position is at the start (^) or end ($) of lines." }
      ]}
    >
      <div className="grid md:grid-cols-3 gap-6">
        {categories.map(c => (
          <div key={c.name} className="border border-slate-200 rounded-2xl p-5 bg-white shadow-xs">
            <h4 className="font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100">{c.name}</h4>
            <div className="space-y-3">
              {c.items.map(item => (
                <div key={item.k} className="flex gap-3 text-xs items-center">
                  <span className="font-mono text-indigo-600 bg-indigo-50 border border-indigo-100 px-2 py-1 rounded-md shrink-0 font-bold">{item.k}</span>
                  <span className="text-slate-600 font-medium">{item.v}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </ToolPageLayout>
  );
}

export function RegexGenerator() {
  const [startsWith, setStartsWith] = useState("https://");
  const [contains, setContains] = useState("domain");
  const [endsWith, setEndsWith] = useState(".com");
  const [allowNumbers, setAllowNumbers] = useState(true);
  const [allowLetters, setAllowLetters] = useState(true);
  const [copied, setCopied] = useState(false);

  let pattern = "^";
  if (startsWith) pattern += startsWith.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  pattern += ".*";
  if (contains) pattern += contains.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ".*";
  
  if (!allowNumbers || !allowLetters) {
    if (!allowNumbers && !allowLetters) {
      pattern = "^$"; // Nothing allowed?
    } else if (!allowNumbers) {
      pattern = "^[^0-9]*$"; 
    } else if (!allowLetters) {
      pattern = "^[^a-zA-Z]*$";
    }
  }

  if (endsWith) {
     if (pattern.endsWith(".*")) pattern = pattern.slice(0, -2);
     pattern += ".*" + endsWith.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  pattern += "$";
  
  if (!startsWith && !contains && !endsWith && allowNumbers && allowLetters) {
    pattern = "^.*$";
  }

  const handleCopy = () => {
    copyToClipboard(pattern).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Regex Generator Online - Build Regular Expressions"
      description="Build custom regular expressions easily using our structured wizard. Generate regex checks for prefixes, sub-strings, endings, and characters."
      h1="Regex Generator"
      category="Regex"
      categoryPath="/regex-tester"
      howToUse={[
        "Type your matching boundary values (startsWith, contains, endsWith).",
        "Toggle character restriction options like allowing letters or numbers.",
        "Copy your generated regular expression pattern instantly."
      ]}
      faq={[
        { question: "How does the generator escape strings?", answer: "It automatically inserts backslashes before special characters to avoid breaking regex structures." },
        { question: "Is this tool suitable for url checking?", answer: "Yes. Simply input prefixes like 'https://' to secure a clean URL domain match structure." }
      ]}
    >
      <div className="max-w-2xl mx-auto space-y-6 bg-white border border-slate-200 p-6 rounded-2xl shadow-xs">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Must Start With</Label>
            <Input value={startsWith} onChange={e => setStartsWith(e.target.value)} placeholder="e.g. https://" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Must Contain</Label>
            <Input value={contains} onChange={e => setContains(e.target.value)} placeholder="e.g. key" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-700">Must End With</Label>
            <Input value={endsWith} onChange={e => setEndsWith(e.target.value)} placeholder="e.g. .com" />
          </div>
        </div>
        
        <div className="flex gap-6 pt-2 border-t border-slate-100">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={allowLetters} onChange={e => setAllowLetters(e.target.checked)} className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500" />
            <span className="text-sm font-medium text-slate-700">Allow letters (a-z)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={allowNumbers} onChange={e => setAllowNumbers(e.target.checked)} className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500" />
            <span className="text-sm font-medium text-slate-700">Allow numbers (0-9)</span>
          </label>
        </div>

        <div className="pt-4 border-t border-slate-100 space-y-2">
          <div className="flex justify-between items-center">
            <Label className="text-sm font-semibold text-slate-700">Generated Regex Pattern</Label>
            <Button variant="outline" size="sm" onClick={handleCopy} className="text-xs gap-1 py-1 h-7">
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Pattern"}
            </Button>
          </div>
          <Input value={pattern} readOnly className="font-mono bg-slate-50 text-indigo-600 font-bold py-6 text-center text-lg" />
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function RegexTools() {
  return (
    <ToolPageLayout
      title="Regular Expression (Regex) Suite Online"
      description="Construct, test, and master regular expressions with our complete suite of regex utilities: tester, cheat sheet, and custom wizards."
      h1="Regex Utilities Suite"
      category="Regex"
      categoryPath="/regex-tester"
      howToUse={[
        "Toggle or review the regex modules below.",
        "Input candidate strings or select anchor patterns for instantaneous evaluation."
      ]}
      faq={[
        { question: "Where is regex handled?", answer: "100% offline inside your browser RAM sandbox, securing complete privacy." }
      ]}
    >
      <div className="space-y-16">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Regex Tester</h2><RegexTester /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Regex Cheat Sheet</h2><RegexCheatSheet /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Regex Generator</h2><RegexGenerator /></section>
      </div>
    </ToolPageLayout>
  );
}
